import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ReactNode } from 'react';

/** 警告弹窗底部按钮（样式类名对应 `FJWarningModal.module.less` 中的 `.btn_box` 修饰符） */
export type FJWarningModalFooterBtn = {
    /** 对应 less 中 `.btn_box` 的修饰类名，空字符串表示仅基础样式 */
    btnType?: string;
    btnText: string;
    btnCallback?: () => void | Promise<void>;
};

export type FJWarningModalData = {
    title: string;
    content: ReactNode;
    display: boolean;
    footerDates?: FJWarningModalFooterBtn[];
    closeCallback?: () => void;
};

export type FJProgressModalData = {
    display: boolean;
    /** 文案键，由 UI 侧 `FJLocalStore._(messageKey)` 取值 */
    messageKey: string;
    hint: string;
    /** 0–100 */
    progress: number;
};

/** 重命名弹窗业务上下文（仅可序列化字段，确认逻辑在 `FJRenameModalContainer`） */
export type FJRenameModalScope = 'project' | 'details-clip';

/** 重命名弹窗（最近文件标题 / 详情片段标题） */
export type FJRenameModalData = {
    display: boolean;
    /** 编辑框初始文案 */
    initialTitle: string;
    context: FJRenameModalScope | '';
    /** 最近文件 id 或详情页片段 id */
    targetId: string;
};

export type FJDislikeModalData = {
    display: boolean;
    /** 目标详情片段 id（提交逻辑在 `FJDislikeReasonsModalContainer` + `FJDetailsPageAction`） */
    targetDetailId: string;
};

export type FJModalsState = {
    warningModalData: FJWarningModalData;
    progressModalData: FJProgressModalData;
    renameModalData: FJRenameModalData;
    dislikeModalData: FJDislikeModalData;
};

const initialWarningModalData: FJWarningModalData = {
    title: '',
    content: null,
    display: false,
    footerDates: undefined,
    closeCallback: undefined,
};

const initialProgressModalData: FJProgressModalData = {
    display: false,
    messageKey: '',
    hint: '',
    progress: 0,
};

const initialRenameModalData: FJRenameModalData = {
    display: false,
    initialTitle: '',
    context: '',
    targetId: '',
};

const initialDislikeModalData: FJDislikeModalData = {
    display: false,
    targetDetailId: '',
};

const initialState: FJModalsState = {
    warningModalData: initialWarningModalData,
    progressModalData: initialProgressModalData,
    renameModalData: initialRenameModalData,
    dislikeModalData: initialDislikeModalData,
};

const fjModalsSlice = createSlice({
    name: 'modals',
    initialState,
    reducers: {
        showWarningModal: (state, action: PayloadAction<Omit<FJWarningModalData, 'display'>>) => {
            state.warningModalData = {
                ...initialWarningModalData,
                ...action.payload,
                display: true,
            };
        },
        hideWarningModal: state => {
            state.warningModalData = {
                ...initialWarningModalData,
            };
        },
        showProgressModal: (state, action: PayloadAction<Omit<FJProgressModalData, 'display'>>) => {
            state.progressModalData = {
                ...initialProgressModalData,
                ...action.payload,
                display: true,
            };
        },
        setProgressModalProgress: (state, action: PayloadAction<{ progress: number }>) => {
            if (!state.progressModalData.display) return;
            state.progressModalData.progress = action.payload.progress;
        },
        hideProgressModal: state => {
            state.progressModalData = { ...initialProgressModalData };
        },
        showRenameModal: (state, action: PayloadAction<Omit<FJRenameModalData, 'display'>>) => {
            state.renameModalData = {
                ...initialRenameModalData,
                ...action.payload,
                display: true,
            };
        },
        hideRenameModal: state => {
            state.renameModalData = { ...initialRenameModalData };
        },
        showDislikeModal: (state, action: PayloadAction<{ targetDetailId: string }>) => {
            state.dislikeModalData = {
                ...initialDislikeModalData,
                ...action.payload,
                display: true,
            };
        },
        hideDislikeModal: state => {
            state.dislikeModalData = { ...initialDislikeModalData };
        },
    },
});

export const {
    showWarningModal,
    hideWarningModal,
    showProgressModal,
    setProgressModalProgress,
    hideProgressModal,
    showRenameModal,
    hideRenameModal,
    showDislikeModal,
    hideDislikeModal,
} = fjModalsSlice.actions;

export default fjModalsSlice.reducer;
